import {
  FormControl,
  FormHelperText,
  FormLabel,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useHelperTextClasses } from '../BaseTextField';
import { useField } from 'formik';
import { Check } from '@mui/icons-material';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import React, { useCallback, useMemo } from 'react';
import { Chip } from '../../Chip';

export type ChipGroupProps = {
  name: string;
  label?: string;
  className?: string;
  error?: boolean;
  helperText?: string;
  hideHelper?: boolean;
  disabled?: boolean;
  color?: 'primary' | 'secondary';
  options: (ChipGroupOption | string)[];
  // Suppress error visuals (red borders + validation message) while leaving
  // validation itself intact — for forms where a disabled submit button is
  // the only invalid-state affordance.
  showError?: boolean;
};

export type ChipGroupOption = {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  className?: string;
};

export const ChipGroupRaw: React.VFC<Omit<ChipGroupProps, 'error' | 'name'> & {
  touched?: boolean;
  error?: string;
  value: unknown[];
  setValue: (value: unknown[], shouldValidate?: boolean) => void;
  setTouched?: (isTouched: boolean, shouldValidate?: boolean) => void;
}> = ({ color = 'secondary', showError = true, ...props }) => {
  const { classes: helperTextClasses } = useHelperTextClasses({});
  const { classes, cx } = useChipGroupStyles({ color }, { props: { color } });
  const hasError = showError && (Boolean(props.error && props.helperText) || Boolean(props.error && props.touched));
  const helperText = (showError && props.touched && props.error) || (!props.hideHelper && props.helperText);

  const optionsToMap = useMemo(() => props.options.map((opt) => {
    return typeof opt === 'string' ? { label: opt, value: opt } : opt;
  }), [ props.options ]);

  const onChange = useCallback((value: string, checked: boolean) => {
    const values = Array.isArray(props.value) ? props.value : [];
    const updatedValues = checked ? [ ...values, value ] : values.filter(v => !isEqual(v, value));
    const sorted = sortBy(updatedValues, (updatedValue) => {
      return optionsToMap.findIndex(option => isEqual(option.value, updatedValue));
    });

    props.setValue(sorted, true);
    props.setTouched?.(true, false);
  }, [ props.value, props.setValue, props.setTouched, optionsToMap ]);

  return (
    <FormControl
      component="fieldset"
      error={hasError}
      disabled={props.disabled}
      color={color}
      className={props.className}
      fullWidth
    >
      <FormLabel focused={false}>{props.label}</FormLabel>
      <div className={classes.chipRow}>
        {optionsToMap.map(({ label, value, disabled, className }, index) => {
          const selected = includes(props.value, value);

          return (
            <Chip
              key={index}
              label={label}
              clickable
              variant="outlined"
              disabled={props.disabled || disabled}
              onClick={() => onChange(value, !selected)}
              aria-pressed={selected}
              icon={selected ? <Check fontSize="small" /> : undefined}
              className={cx(classes.chip, className, {
                [classes.selected]: selected,
                [classes.error]: hasError,
              })}
            />
          );
        })}
      </div>
      {helperText && (
        <FormHelperText className={helperTextClasses.root}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const ChipGroup: React.VFC<ChipGroupProps> = (props) => {
  const [ field, meta, helpers ] = useField<unknown[]>(props.name);

  return (
    <ChipGroupRaw
      {...props}
      value={field.value}
      setValue={helpers.setValue}
      touched={meta.touched}
      error={meta.error}
      setTouched={helpers.setTouched}
    />
  );
};

const useChipGroupStyles = makeStyles<Pick<ChipGroupProps, 'color'>>({ name: 'ChipGroup' })((theme, props) => ({
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  chip: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[400]}`,
    color: theme.palette.text.primary,
  },
  selected: {
    background: props.color
      ? theme.palette[props.color].background
      : undefined,
    borderColor: props.color
      ? theme.palette[props.color].main
      : undefined,
    color: props.color
      ? theme.palette[props.color].dark
      : undefined,
    '& .MuiChip-icon': {
      color: props.color
        ? theme.palette[props.color].dark
        : undefined,
    },
  },
  error: {
    borderColor: theme.palette.error.main,
  },
}));
